import { useTranslation } from 'react-i18next';
import { useParams } from '@tanstack/react-router';
import { InfoGrid, InfoRow } from '@/components';
import { PageContentCard } from '@/components/layout/PageContentCard';
import { formatDate } from '@/utils/dateUtils';
import { useDeviceModels } from '@/features/device-models';
import { ServiceContractCard } from '@/features/service-contracts';
import { RepairHistoryCard } from '@/features/repair-history';
import { useDeviceDetails } from '../hooks';

// DeviceOverviewTab shows the device's core metadata alongside its service contract state.
export function DeviceOverviewTab() {
  const { t } = useTranslation();
  const { deviceId } = useParams({ strict: false }) as { deviceId: string };

  const { data: details, isLoading } = useDeviceDetails(deviceId);
  const { data: deviceModels } = useDeviceModels();

  const modelName = deviceModels?.find(
    (model) => model.id === details?.device.modelId
  )?.name;

  if (isLoading || !details) {
    return (
      <PageContentCard className="w-full">
        <div className="animate-pulse space-y-4">
          <div className="bg-border h-4 w-1/4 rounded" />
          <div className="bg-border h-10 rounded" />
          <div className="bg-border h-10 rounded" />
        </div>
      </PageContentCard>
    );
  }

  const { device, company } = details;

  return (
    <div className="w-full space-y-4">
      <PageContentCard className="w-full">
        <h2 className="text-onsurface mb-2 text-lg font-semibold">
          {t('devices.detail.overview.deviceInfo')}
        </h2>
        <InfoGrid>
          <InfoRow
            label={t('devices.detail.overview.serialNumber')}
            value={String(device.serialNumber)}
          />
          <InfoRow
            label={t('devices.detail.overview.model')}
            value={modelName ?? '—'}
          />
          <InfoRow
            label={t('devices.detail.overview.company')}
            value={company.name}
          />
          <InfoRow
            label={t('devices.detail.overview.productionDate')}
            value={formatDate(device.productionDate ?? undefined)}
          />
          <InfoRow
            label={t('devices.detail.overview.deliveryDate')}
            value={formatDate(device.deliveryDate ?? undefined)}
          />
          <InfoRow
            label={t('devices.detail.overview.warrantyStartedAt')}
            value={formatDate(device.warrantyStartedAt ?? undefined)}
          />
          <InfoRow
            label={t('devices.detail.overview.lastOnline')}
            value={formatDate(device.lastOnline ?? undefined)}
          />
        </InfoGrid>
      </PageContentCard>

      <ServiceContractCard
        deviceId={deviceId}
        company={{ id: company.id, name: company.name }}
        serviceContract={details.serviceContract}
      />

      <RepairHistoryCard deviceId={deviceId} />
    </div>
  );
}
